import { PageLayout, TopBar, Pagination, DetailsDrawer } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { useSelectedInneEvent } from 'hooks/useSelectedInneEvent/useSelectedInneEvent';
import { AppMessages } from 'i18n/messages';
import { AppRoute } from 'routing/AppRoute.enum';
import { Table } from 'ui/table/Table';

import { InneEventsProps } from './InneEvents.types';
import { InneEventsTableHeader } from './inneEventsTableHeader/InneEventsTableHeader';
import { InneEventsTableRow } from './inneEventsTableRow/InneEventsTableRow';
import { InneEventsFiltersContainer } from './inneEventsFilters/InneEventsFiltersContainer';
import { InneEventDetails } from './inneEventDetails/InneEventDetails';
import * as Styled from './InneEvents.styles';

export const InneEvents = ({
  inneEventsResponse,
  isLoading,
  rowsPerPage,
  setRowsPerPage,
  page,
  setPage,
  setTableSearchQuery,
  tableSearchQuery,
  tkdSearchQuery,
  setTkdSearchQuery,
}: InneEventsProps) => {
  const { selectedEvent, setSelectedEvent } = useSelectedInneEvent();
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();
  const displayFullTable = !selectedEvent;
  const canViewEvents = !!perms?.ZDARZENIA?.view;
  const canViewPresence = !!perms?.OBECNOSCI?.view;
  const tabsCount = (canViewEvents ? 3 : 1) + (canViewPresence ? 1 : 0);

  return (
    <PageLayout
      header={formatMessage({ id: AppMessages['events.title'] })}
      renderContent={() => (
        <>
          <Styled.TopBarWrapper gridTemplateColumns={`repeat(${tabsCount}, auto) 1fr`}>
            {canViewEvents && (
              <>
                <TopBar.Link linkTo={AppRoute.events} isActive={false}>
                  {formatMessage({ id: AppMessages['events.current'] })}
                </TopBar.Link>

                <TopBar.Link linkTo={AppRoute.eventsHistory} isActive={false}>
                  {formatMessage({ id: AppMessages['events.history'] })}
                </TopBar.Link>
              </>
            )}

            <TopBar.Link linkTo={AppRoute.eventsOther} isActive>
              {formatMessage({ id: AppMessages['events.other'] })}
            </TopBar.Link>

            {canViewPresence && (
              <TopBar.Link linkTo={AppRoute.eventsCurrentPresence} isActive={false}>
                {formatMessage({ id: AppMessages['events.currentPresence'] })}
              </TopBar.Link>
            )}

            <TopBar.ButtonsWrapper>
              <></>
            </TopBar.ButtonsWrapper>
          </Styled.TopBarWrapper>

          <Table
            isLoading={isLoading}
            gridTemplateColumns={`repeat(${displayFullTable ? 7 : 6}, minmax(100px, 1fr))`}
            data={inneEventsResponse?.data || []}
            nothingToShowText={formatMessage({ id: AppMessages['events.other.nothingToShow'] })}
            activeRowId={selectedEvent?.id}
            onClickRow={(event) => setSelectedEvent(selectedEvent?.id === event.id ? undefined : event)}
            renderHeader={() => <InneEventsTableHeader displayFullTable={displayFullTable} />}
            renderRow={(event) => <InneEventsTableRow event={event} displayFullTable={displayFullTable} />}
            renderAdditionalTopElement={() => (
              <>
                <InneEventsFiltersContainer
                  setTableSearchQuery={setTableSearchQuery}
                  tableSearchQuery={tableSearchQuery}
                  setTkdSearchQuery={setTkdSearchQuery}
                  tkdSearchQuery={tkdSearchQuery}
                />

                <Styled.PaginationWrapper>
                  <Pagination
                    page={page}
                    setPage={setPage}
                    count={inneEventsResponse?.count || 0}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                  />
                </Styled.PaginationWrapper>
              </>
            )}
          />
        </>
      )}
      renderDetails={() => (
        <DetailsDrawer isOpen={!!selectedEvent} header={formatMessage({ id: AppMessages['event.details.title'] })}>
          {selectedEvent && <InneEventDetails selectedEvent={selectedEvent} />}
        </DetailsDrawer>
      )}
    />
  );
};
